import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { IntelMonitor, MONITOR_LABELS, MonitorKind } from '@signals/app-shell/services/intelPacketApi';
import { MONITOR_CATALOG } from './monitorCatalog';
import { formatNewsQueryPreview, NewsQuery, newsQueryHasTerms, parseNewsQuery } from './NewsQueryBuilderDialog';

const BUFFER_MAX_M = 2500;

interface MonitorInfoDialogProps {
  open: boolean;
  monitor: IntelMonitor | null;
  bufferMeters: number;
  onClose: () => void;
  onBufferChange?: (meters: number) => void;
  onBufferCommit?: (meters: number) => void;
  onEditNewsTerms?: () => void;
  onClearNewsTerms?: () => void;
  formatBuffer: (meters: number) => string;
}

const MonitorInfoDialog: React.FC<MonitorInfoDialogProps> = ({
  open,
  monitor,
  bufferMeters,
  onClose,
  onBufferChange,
  onBufferCommit,
  onEditNewsTerms,
  onClearNewsTerms,
  formatBuffer,
}) => {
  if (!monitor) return null;
  const kind = monitor.kind as MonitorKind;
  const entry = MONITOR_CATALOG[kind];
  const newsQ = parseNewsQuery(monitor.thresholds?.news_query) as NewsQuery;
  const newsPreview = formatNewsQueryPreview(newsQ);
  const showBuffer = Boolean(onBufferChange && onBufferCommit);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: '#121212',
          backgroundImage: 'linear-gradient(180deg, rgba(229,57,53,0.08), transparent 28%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>{MONITOR_LABELS[kind] || kind}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '12px !important' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {monitor.last_run_at ? `Last run ${new Date(monitor.last_run_at).toLocaleString()}` : 'Never run'}
          {monitor.last_error ? ` · ${monitor.last_error}` : ''}
        </Typography>
        {entry && (
          <>
            <Box>
              <Typography variant="caption" sx={{ letterSpacing: 0.8, fontWeight: 700, color: 'rgba(255,255,255,0.45)' }}>
                WHAT IT DOES
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{entry.summary}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {entry.collects}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ letterSpacing: 0.8, fontWeight: 700, color: 'rgba(255,255,255,0.45)' }}>
                FUNCTIONS
              </Typography>
              {entry.functions.map((fn) => (
                <Box key={fn.name} sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fn.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {fn.detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
        {(showBuffer || kind === 'news') && (
          <Box>
            <Typography variant="caption" sx={{ letterSpacing: 0.8, fontWeight: 700, color: 'rgba(255,255,255,0.45)' }}>
              SETTINGS
            </Typography>
            {showBuffer && (
              <Box sx={{ px: 0.5, pt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Buffer from geofence</Typography>
                  <Typography variant="caption" sx={{ color: '#e53935', fontWeight: 700 }}>
                    {formatBuffer(bufferMeters)}
                  </Typography>
                </Box>
                <Slider
                  size="small"
                  value={bufferMeters}
                  min={0}
                  max={BUFFER_MAX_M}
                  step={50}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 1000, label: '1 km' },
                    { value: 2500, label: '2.5 km' },
                  ]}
                  onChange={(_, v) => onBufferChange?.(v as number)}
                  onChangeCommitted={(_, v) => onBufferCommit?.(v as number)}
                  sx={{
                    color: '#e53935',
                    '& .MuiSlider-markLabel': { fontSize: '0.6rem', color: 'text.secondary' },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {entry?.settings || '0 stays inside the fence.'}
                </Typography>
              </Box>
            )}
            {kind === 'news' && (
              <Box sx={{ mt: 1.25 }}>
                {newsPreview ? (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', fontFamily: 'ui-monospace, Menlo, monospace', mb: 0.75, wordBreak: 'break-word' }}
                  >
                    {newsPreview}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    No media terms — all AOI reports.
                  </Typography>
                )}
                <Button
                  size="small"
                  startIcon={<AutoFixHighIcon sx={{ fontSize: 14 }} />}
                  onClick={onEditNewsTerms}
                  sx={{ textTransform: 'none', color: '#e53935', minWidth: 0, mr: 1 }}
                >
                  {newsQueryHasTerms(newsQ) ? 'Edit terms' : 'Set terms'}
                </Button>
                {newsQueryHasTerms(newsQ) && onClearNewsTerms && (
                  <Button size="small" onClick={onClearNewsTerms} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                    Clear
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#e53935', textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonitorInfoDialog;
